// Sales History report (Jake 2026-10-31) — past sales, cost, gross profit and
// margin from commonsku's history (the CS Import archive) and Flow's own
// invoices, together. Admins only. Every number is commonsku's own figure or
// Flow's invoice/bill — see apps/api/src/services/sales-history.ts.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/Button";
import { validCustomRange } from "@/components/ReportShell";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Source = "all" | "commonsku" | "flow";
type Period = "all" | "this_year" | "last_year" | "last_12" | "custom";

interface Totals {
  invoices: number;
  sales: string;
  cost: string;
  profit: string;
  marginPct: string | null;
}

interface Report {
  range: { from: string | null; to: string | null };
  source: Source;
  totals: Totals;
  bySource: Array<{ source: "commonsku" | "flow" } & Totals>;
  byYear: Array<{ year: string } & Totals>;
  byMonth: Array<{ month: string } & Totals>;
  byRep: Array<{ rep: string } & Totals>;
  topClients: Array<{ client: string } & Totals>;
  clientCount: number;
  commonskuAccounts: number;
}

const PERIODS: Array<[Period, string]> = [
  ["all", "All time"],
  ["this_year", "This year"],
  ["last_year", "Last year"],
  ["last_12", "Last 12 months"],
  ["custom", "Your own dates"],
];
const SOURCES: Array<[Source, string]> = [
  ["all", "Everything"],
  ["commonsku", "commonsku history"],
  ["flow", "Flow"],
];

const iso = (d: Date) => d.toISOString().slice(0, 10);

function rangeOf(p: Period, custom: { from: string; to: string } | null): { from?: string; to?: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  if (p === "this_year") return { from: `${y}-01-01`, to: `${y}-12-31` };
  if (p === "last_year") return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  if (p === "last_12") {
    const start = new Date(Date.UTC(y, now.getUTCMonth() - 11, 1));
    return { from: iso(start), to: iso(now) };
  }
  if (p === "custom" && custom) return custom;
  return {};
}

const usd = (v: string) =>
  Number(v).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const pct = (v: string | null) => (v === null ? "—" : `${v}%`);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthName = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

function Chips<T extends string>({ label, options, value, onChange }: { label: string; options: Array<[T, string]>; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-20 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">{label}</span>
      {options.map(([k, text]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors",
            value === k ? "bg-brown-500 text-cream-50" : "bg-cream-100 text-brown-400 hover:bg-cream-200",
          )}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-brown-100 bg-white p-5">
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">{label}</div>
      <div className="mt-2 truncate font-display text-2xl font-semibold text-brown-500 xl:text-3xl">{value}</div>
      {hint && <p className="mt-1 text-xs text-brown-400">{hint}</p>}
    </div>
  );
}

function MoneyTable<R extends Totals>({
  title,
  first,
  rows,
  name,
  onPick,
  footer,
}: {
  title: string;
  first: string;
  rows: R[];
  name: (r: R) => string;
  onPick?: (r: R) => void;
  footer?: string;
}) {
  return (
    <section className="rounded-lg border border-brown-100 bg-white">
      <h2 className="border-b border-brown-100 px-5 py-3 font-display text-xl font-bold text-brown-500">{title}</h2>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-brown-300">No invoices in this window.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-base text-ink">
            <thead className="border-b border-brown-100 bg-cream-50">
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-muted">
                <th className="px-5 py-3 font-medium">{first}</th>
                <th className="px-3 py-3 text-right font-medium">Invoices</th>
                <th className="px-3 py-3 text-right font-medium">Sales</th>
                <th className="px-3 py-3 text-right font-medium">Cost</th>
                <th className="px-3 py-3 text-right font-medium">Gross profit</th>
                <th className="px-5 py-3 text-right font-medium">Margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={name(r)} className={cn("border-t border-brown-50", onPick && "hover:bg-cream-50")}>
                  <td className="px-5 py-3 font-semibold text-brown-500">
                    {onPick ? (
                      <button type="button" onClick={() => onPick(r)} className="underline decoration-brown-200 underline-offset-2 hover:text-ink">
                        {name(r)}
                      </button>
                    ) : (
                      name(r)
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-brown-400">{r.invoices.toLocaleString()}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-ink">{usd(r.sales)}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-brown-400">{usd(r.cost)}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-ink">{usd(r.profit)}</td>
                  <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums text-brown-400">{pct(r.marginPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {footer && <p className="border-t border-brown-100 px-5 py-3 text-xs text-brown-300">{footer}</p>}
    </section>
  );
}

export function ReportSalesHistoryPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes("admin");
  const [period, setPeriod] = useState<Period>("all");
  const [source, setSource] = useState<Source>("all");
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const [draft, setDraft] = useState({ from: "", to: "" });
  const range = rangeOf(period, custom);
  const qs = new URLSearchParams({ source, ...(range.from ? { from: range.from } : {}), ...(range.to ? { to: range.to } : {}) });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "sales-history", qs.toString()],
    queryFn: () => api<Report>(`/reports/sales-history?${qs.toString()}`),
    enabled: isAdmin && (period !== "custom" || custom !== null),
    placeholderData: keepPreviousData,
  });

  const pickYear = (year: string) => {
    const r = { from: `${year}-01-01`, to: `${year}-12-31` };
    setCustom(r);
    setDraft(r);
    setPeriod("custom");
  };

  return (
    <div className="mx-auto w-full">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/reports">
          <ArrowLeft className="h-4 w-4" /> All reports
        </Link>
      </Button>

      <div className="mb-6">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Reports</p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">Sales History</h1>
        <p className="mt-2 max-w-3xl text-sm text-brown-400">
          Sales, cost, gross profit and margin from every invoice, from commonsku's history and Flow, on one screen.
          Sales are before tax. commonsku figures are commonsku's own; Flow's cost is the vendor bills once they're in,
          otherwise the cost entered on the quote.
        </p>
      </div>

      {!isAdmin ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-400">
          Sales History is for admins only.
        </div>
      ) : (
        <>
          <div className="mb-6 space-y-3">
            <Chips
              label="Period"
              options={PERIODS}
              value={period}
              onChange={(p) => {
                if (p === "custom" && !custom) setDraft({ from: `${new Date().getUTCFullYear()}-01-01`, to: iso(new Date()) });
                setPeriod(p);
              }}
            />
            {period === "custom" && (
              <form
                className="flex flex-wrap items-center gap-2 pl-[5.5rem]"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (validCustomRange(draft.from, draft.to)) setCustom(draft);
                }}
              >
                <input type="date" aria-label="From date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className="h-9 rounded-md border border-rule bg-white px-2 text-sm" />
                <span className="text-xs text-brown-300">to</span>
                <input type="date" aria-label="To date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="h-9 rounded-md border border-rule bg-white px-2 text-sm" />
                <Button type="submit" size="sm" disabled={!validCustomRange(draft.from, draft.to)}>Show</Button>
              </form>
            )}
            <Chips label="Source" options={SOURCES} value={source} onChange={setSource} />
          </div>

          {isLoading || (period === "custom" && !custom) ? (
            <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">
              {period === "custom" && !custom ? "Pick your dates." : "Loading…"}
            </div>
          ) : isError || !data ? (
            <div className="rounded-lg border border-rust-300 bg-rust-50 p-10 text-center text-sm text-rust-600">Couldn't load report.</div>
          ) : (
            <div className="space-y-6">
              {source !== "flow" && (
                <p className="rounded-lg border border-peach-200 bg-peach-50 px-4 py-2 text-sm text-brown-500">
                  commonsku history covers {data.commonskuAccounts.toLocaleString()} accounts so far — the rest are still
                  being loaded, so older years will grow.
                </p>
              )}
              <p className="text-xs text-brown-300">
                {data.range.from ? `${data.range.from} → ${data.range.to ?? "today"}` : "All time"}
              </p>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
                <Tile label="Sales" value={usd(data.totals.sales)} hint="before tax" />
                <Tile label="Cost" value={usd(data.totals.cost)} />
                <Tile label="Gross profit" value={usd(data.totals.profit)} />
                <Tile label="Margin" value={pct(data.totals.marginPct)} />
                <Tile
                  label="Invoices"
                  value={data.totals.invoices.toLocaleString()}
                  hint={data.totals.invoices ? `avg ${usd(String(Number(data.totals.sales) / data.totals.invoices))}` : undefined}
                />
              </div>

              {source === "all" && (
                <MoneyTable
                  title="By source"
                  first="Source"
                  rows={data.bySource}
                  name={(r) => (r.source === "commonsku" ? "commonsku history" : "Flow")}
                />
              )}

              <MoneyTable
                title="By year"
                first="Year"
                rows={data.byYear}
                name={(r) => r.year}
                onPick={data.byYear.length > 1 ? (r) => pickYear(r.year) : undefined}
                footer={data.byYear.length > 1 ? "Pick a year to see its months." : undefined}
              />

              {data.byYear.length <= 2 && (
                <MoneyTable title="By month" first="Month" rows={data.byMonth} name={(r) => monthName(r.month)} />
              )}

              <MoneyTable title="By rep" first="Rep" rows={data.byRep} name={(r) => r.rep} />

              <MoneyTable
                title="Top clients"
                first="Client"
                rows={data.topClients}
                name={(r) => r.client}
                footer={`Top ${data.topClients.length} of ${data.clientCount.toLocaleString()} clients by sales.`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
